import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { Button } from "react-bootstrap";
import {
  getAllCajaSalida,
  insertCajaSalida,
  updateCajaSalida,
  deleteCajaSalida,
} from "../../../api/InventarioApi";
import ModalCajaSalida from "./ModalCajaSalida";

function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      className="form-control form-control-sm"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="Filtrar..."
      style={{ minWidth: 80 }}
    />
  );
}

const formatFecha = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("es-AR");
};

export default function CajaSalida() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const tableRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getAllCajaSalida();
      setData(res || []);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelected(null);
    setIsNew(true);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelected(item);
    setIsNew(false);
    setShowModal(true);
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "Eliminar Salida",
      text: `¿Desea eliminar la salida de "${item.articulo}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteCajaSalida(item.idcajasalida);
          await loadData();
          Swal.fire("Eliminado", "Salida eliminada correctamente", "success");
        } catch (err) {
          Swal.fire("Error", err.message, "error");
        }
      }
    });
  };

  const handleSave = async (formData) => {
    setShowModal(false);
    try {
      if (isNew) await insertCajaSalida(formData);
      else await updateCajaSalida(formData.idcajasalida, formData);
      await loadData();
      Swal.fire("Éxito", "Guardado correctamente", "success");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const handleExportExcel = () => {
    if (!data.length) return Swal.fire("Sin datos", "No hay datos para exportar", "info");
    const mapped = data.map((it) => ({
      ID: it.idcajasalida,
      Artículo: it.articulo,
      Cantidad: it.cantidad,
      Fecha: formatFecha(it.fecha),
      Motivo: it.motivo,
      Destino: it.destino,
      Observación: it.observacion,
    }));
    const ws = XLSX.utils.json_to_sheet(mapped);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CajaSalida");
    XLSX.writeFile(wb, "CajaSalida.xlsx");
  };

  const columns = useMemo(
    () => [
      { Header: "Artículo", accessor: "articulo", minWidth: 150 },
      { Header: "Cantidad", accessor: "cantidad", minWidth: 80, Cell: ({ value }) => <span className="badge bg-danger" style={{ fontSize: "0.85rem" }}>{value}</span> },
      { Header: "Fecha", accessor: "fecha", minWidth: 100, Cell: ({ value }) => formatFecha(value) },
      { Header: "Motivo", accessor: "motivo", minWidth: 130 },
      { Header: "Destino", accessor: "destino", minWidth: 130 },
      { Header: "Observación", accessor: "observacion", minWidth: 180 },
      {
        Header: "Opciones",
        disableFilters: true,
        width: 120,
        Cell: ({ row }) => (
          <div className="d-flex justify-content-center gap-2">
            <button className="btn btn-primary btn-sm" onClick={() => handleEdit(row.original)} title="Editar">
              <i className="fas fa-pencil-alt"></i>
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(row.original)} title="Eliminar">
              <i className="fa fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const tableInstance = useTable(
    { columns, data, defaultColumn, initialState: { pageSize: 10 } },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps, getTableBodyProps, headerGroups, page, prepareRow,
    state, setGlobalFilter, gotoPage, pageCount, nextPage, previousPage,
    canNextPage, canPreviousPage, pageOptions,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  return (
    <div className="opciones-container">
      <div className="opciones-header">
        <h5>Caja de Salida</h5>
        <div className="d-flex gap-2">
          <Button size="sm" variant="primary" onClick={handleAdd}>+ Agregar</Button>
          <Button size="sm" variant="outline-success" onClick={handleExportExcel}>Exportar Excel</Button>
        </div>
      </div>

      <div className="mb-2 opciones-search">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar..."
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
          <span style={{ marginLeft: 14, color: "#444", fontSize: 18 }}>Cargando...</span>
        </div>
      ) : (
        <div className="tabla-scroll">
          <table {...getTableProps()} className="table table-sm tabla-opciones" ref={tableRef}>
            <thead>
              {headerGroups.map((hg, i) => (
                <React.Fragment key={i}>
                  <tr {...hg.getHeaderGroupProps()} className="header-row">
                    {hg.headers.map((col) => (
                      <th key={col.id}>{col.render("Header")}</th>
                    ))}
                  </tr>
                  <tr className="filter-row">
                    {hg.headers.map((col) => (
                      <th key={`${col.id}-filter`}>
                        {col.canFilter ? col.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, ri) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={ri}>
                    {row.cells.map((cell, ci) => (
                      <td {...cell.getCellProps()} key={ci}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })}
              {page.length === 0 && (
                <tr><td colSpan={columns.length} className="text-center text-muted py-3">Sin registros</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="opciones-paginacion">
        <span>Página <strong>{pageIndex + 1} de {pageOptions.length || 1}</strong></span>
        <div>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => previousPage()} disabled={!canPreviousPage}>Anterior</button>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => nextPage()} disabled={!canNextPage}>Siguiente</button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>
        </div>
      </div>

      {showModal && (
        <ModalCajaSalida
          estado={showModal}
          onCloseModal={() => setShowModal(false)}
          onReturnValue={handleSave}
          item={selected}
          isnew={isNew}
        />
      )}
    </div>
  );
}
